import { cn } from '@/lib/utils';

interface GaugeChartProps {
  /** Value in [0, 1]. null / undefined → unknown state. */
  value: number | null | undefined;
  label?: string;
  size?: number;
  className?: string;
}

export function GaugeChart({ value, label, size = 120, className }: GaugeChartProps) {
  const clamped = value == null || Number.isNaN(value) ? null : Math.max(0, Math.min(1, value));
  const angle = clamped == null ? 0 : clamped * 180;
  const radius = size / 2 - 8;
  const circ = Math.PI * radius;
  const offset = circ * (1 - (clamped ?? 0));
  const tone =
    clamped == null
      ? 'hsl(var(--muted-foreground))'
      : clamped >= 0.66
        ? 'hsl(var(--success))'
        : clamped >= 0.33
          ? 'hsl(var(--warning))'
          : 'hsl(var(--destructive))';

  return (
    <div
      className={cn('flex flex-col items-center gap-1 text-xs text-muted-foreground', className)}
      role="img"
      aria-label={label ? `${label} ${clamped == null ? 'unknown' : Math.round(clamped * 100) + '%'}` : undefined}
    >
      <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
        <path
          d={describeArc(size / 2, size / 2, radius, -90, 90)}
          stroke="hsl(var(--muted))"
          strokeWidth={8}
          fill="none"
          strokeLinecap="round"
        />
        <path
          d={describeArc(size / 2, size / 2, radius, -90, -90 + angle)}
          stroke={tone}
          strokeWidth={8}
          fill="none"
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease', strokeDasharray: circ, strokeDashoffset: offset }}
        />
      </svg>
      <div className="-mt-4 text-center">
        <div className="font-display text-xl font-extrabold" style={{ color: tone }}>
          {clamped == null ? '—' : `${Math.round(clamped * 100)}%`}
        </div>
        {label && <div className="text-[11px] uppercase tracking-wider">{label}</div>}
      </div>
    </div>
  );
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const startRad = (Math.PI / 180) * startDeg;
  const endRad = (Math.PI / 180) * endDeg;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}
